import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  OverridableCost,
  SpellOverrides,
  SpellsFromListWithCosts,
  SpellWithOverridableCost,
} from "@dndModels";
import { SpellLevel } from "@dndModels/Types";
import { LEVELED_SPELL_LEVELS } from "@dndModels/Constants";

import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import SpellOverridesInput from "@dndComponents/feature/SpellOverridesInput";
import OverridableCostInput from "@dndComponents/feature/OverridableCostInput";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import AddButton from "@components/input/buttons/AddButton";
import DeleteButton from "@components/input/buttons/DeleteButton";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StringBox from "@components/input/strings/StringBox";

interface Props {
  obj: SpellsFromListWithCosts;
  attached_table?: AttachedTable;
  disabled: boolean;
  onChange: (changed: SpellsFromListWithCosts) => void; 
}

export default function SpellsFromListWithCostsInput({
  obj,
  attached_table,
  disabled,
  onChange, 
}: Props) {
  const [expanded, setExpanded] = useState("");
  const [ping, setPing] = useState(false);
  const [mixed, setMixed] = useState(obj.slot_override === "Mixed" || obj.spell_consumed_resource === "Mixed");

  const handleAddSpell = () => {
    const new_obj = obj.clone();
    const s = new SpellWithOverridableCost();
    if (!mixed) {
      s.cost_override.slot_override = new_obj.shared_cost_override.slot_override;
      s.cost_override.consumed_resource.resource_consumed = new_obj.shared_cost_override.consumed_resource.resource_consumed;
    }
    new_obj.spells.push(s);
    onChange(new_obj);
    setExpanded(s.true_id);
    setPing(!ping);
  };

  const handleRemoveSpell = (true_id: string) => {
    const new_obj = obj.clone();
    new_obj.spells = new_obj.spells.filter(o => o.true_id !== true_id);
    onChange(new_obj);
    setPing(!ping);
  };

  const handleToggleMixed = () => {
    const new_mixed = !mixed;
    setMixed(new_mixed);
    
    if (!new_mixed) {
      // When switching from mixed to shared, force all non-At-Will spells to match shared
      const new_obj = obj.clone();
      for (const s of new_obj.spells) {
        if (s.cost_override.slot_override !== "At Will" && s.cost_override.slot_override !== "At Will or Slot Upcast") {
          s.cost_override.slot_override = new_obj.shared_cost_override.slot_override;
          s.cost_override.consumed_resource.resource_consumed = new_obj.shared_cost_override.consumed_resource.resource_consumed;
        }
      }
      onChange(new_obj);
      setPing(!ping);
    }
  };

  const renderSpellCost = (spell: SpellWithOverridableCost) => {
    const amount_consumed = spell.cost_override.consumed_resource.amount_consumed;
    const resource_consumed = spell.cost_override.consumed_resource.resource_consumed;
    // debugger;
    if (["Special","None","Recharge"].includes(resource_consumed)) {
      return (
        <>
        </>
      );
    } else if (["Infusion","Legendary Action"].includes(resource_consumed)) {
      return (
        <>
          { amount_consumed }
          &nbsp;
          { resource_consumed }
        </>
      );
    } else if (resource_consumed === "Slot") {
      return (
        <>
          { amount_consumed } Spell Slot{ amount_consumed > 1 && "s" }
        </>
      );
    } else if (resource_consumed === "Pact") {
      return (
        <>
          { amount_consumed } Pact Slot{ amount_consumed > 1 && "s" }
        </>
      );
    } else if (resource_consumed === "Charge") {
      return (
        <>
          { amount_consumed } Charge{ amount_consumed > 1 && "s" }
        </>
      );
    } else {
      return (
        <>
          { amount_consumed }&nbsp;<DisplayObjects type='resource' ids={[resource_consumed]} />
        </>
      );
    }
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <span className="header padded">
          Spells with Individual Costs
        </span>
        <AddButton 
          disabled={disabled || obj.spells.some(o => o.spell_id === "")}
          onClick={handleAddSpell}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ToggleButtonBox
          name="Mixed"
          disabled={disabled}
          value={mixed}
          onToggle={handleToggleMixed}
        />
      </Grid>
      { !mixed &&
        <Grid
          size={{
            xs: 12
          }}>
          <OverridableCostInput
            obj={obj.shared_cost_override}
            no_amount
            disabled={disabled}
            onChange={(changed: OverridableCost) => {
              const new_obj = obj.clone();
              new_obj.shared_cost_override = changed.clone();
              for (const s of new_obj.spells) {
                if (s.cost_override.slot_override !== "At Will" && s.cost_override.slot_override !== "At Will or Slot Upcast") {
                  s.cost_override.slot_override = new_obj.shared_cost_override.slot_override;
                  s.cost_override.consumed_resource.resource_consumed = new_obj.shared_cost_override.consumed_resource.resource_consumed;
                }
              }
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      {obj.spells.map(spell => (
        <Grid
          key={spell.true_id}
          className="padded"
          size={{
            xs: 12
          }}>
          <Grid container spacing={1}>
            <Grid
              className="clickable"
              onClick={() => setExpanded(spell.true_id === expanded ? "" : spell.true_id)}
              size={{
                xs: 11
              }}>
              { spell.spell_id === "" ? "Please Select a Spell" : <DisplayObjects type="spell" ids={[spell.spell_id]} />}
              &nbsp;
              { renderSpellCost(spell) }
              { spell.cast_at_level !== "Don't Override" && ` (Cast at Level ${spell.cast_at_level})` }
            </Grid>
            <Grid
              size={{
                xs: 1
              }}>
              <DeleteButton
                disabled={disabled}
                onClick={() => handleRemoveSpell(spell.true_id)} 
              />
            </Grid>
            { expanded === spell.true_id &&
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <SelectSpellBox 
                    name="Spell" 
                    value={spell.spell_id} 
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj.clone();
                      const s = new_obj.spells.find(o => o.true_id === spell.true_id);
                      if (s) {
                        s.spell_id = changed;
                      }
                      onChange(new_obj);
                      setPing(!ping);
                    }} 
                  />
                </Grid>
                { mixed ?
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <OverridableCostInput
                      obj={spell.cost_override}
                      disabled={disabled}
                      onChange={(changed: OverridableCost) => {
                        const new_obj = obj.clone();
                        const s = new_obj.spells.find(o => o.true_id === spell.true_id);
                        if (s) {
                          s.cost_override = changed.clone();
                        }
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                :
                  <>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <SelectStringBox 
                        name="Slot Override"
                        disabled={disabled}
                        options={["Match","At Will","At Will or Slot Upcast"]}
                        value={spell.cost_override.slot_override === "At Will" || spell.cost_override.slot_override === "At Will or Slot Upcast" ? spell.cost_override.slot_override : "Match"}
                        onChange={(changed: "Match" | "At Will" | "At Will or Slot Upcast") => {
                          const new_obj = obj.clone();
                          const s = new_obj.spells.find(o => o.true_id === spell.true_id);
                          if (s) {
                            if (changed === "Match") {
                              s.cost_override.slot_override = new_obj.shared_cost_override.slot_override;
                              s.cost_override.consumed_resource.resource_consumed = new_obj.shared_cost_override.consumed_resource.resource_consumed;
                            } else {
                              s.cost_override.slot_override = changed;
                            }
                          }
                          onChange(new_obj);
                          setPing(!ping);
                        }}
                      /> 
                    </Grid>
                    <Grid
                      size={{
                        xs: 6
                      }}>
                      <StringBox 
                        name="Amount Consumed"
                        value={spell.cost_override.consumed_resource.amount_consumed} 
                        type="number"
                        disabled={disabled}
                        onBlur={(changed: number) => {
                          const new_obj = obj.clone();
                          const s = new_obj.spells.find(o => o.true_id === spell.true_id);
                          if (s) {
                            s.cost_override.consumed_resource.amount_consumed = +changed;
                          }
                          onChange(new_obj);
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                  </>
                }
                
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <SelectStringBox 
                    name="Cast at Level" 
                    value={`${spell.cast_at_level}`} 
                    options={LEVELED_SPELL_LEVELS}
                    extra_options={["Don't Override"]}
                    disabled={disabled}
                    onChange={(changed: SpellLevel | "Don't Override") => {
                      const new_obj = obj.clone();
                      const s = new_obj.spells.find(o => o.true_id === spell.true_id);
                      if (s) {
                        s.cast_at_level = changed;
                      }
                      onChange(new_obj);
                      setPing(!ping);
                    }} 
                  />
                </Grid>
              </>
            }
          </Grid>
        </Grid>
      ))}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>Spell Alterations</Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SpellOverridesInput
          obj={obj.spell_overrides}
          for_obj={obj}
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: SpellOverrides) => {
            const new_obj = obj.clone();
            new_obj.spell_overrides = changed.clone();
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
    </Grid>
  );
}